import { z } from 'zod';
import { ExamKey, TableColumn, TopicPath } from './fields.js';
import { Uuid } from './primitives.js';
/**
 * Question bank, QC pipeline, generation and review contracts (M3). Money in paise (integer),
 * dates as ISO strings. Option order is the stored order; `correctIndex` is 0-based.
 */
export const ItemStatusName = z.enum([
    'generated',
    'auto_checks',
    'verifying',
    'dedupe',
    'in_review',
    'field_test',
    'published',
    'pulled',
    'rejected',
    'retired',
]);
export const ItemOriginName = z.enum(['generated', 'import', 'manual', 'pyq']);
export const CheckName = z.enum([
    'format',
    'syllabus',
    'verify',
    'solver',
    'consistency',
    'dedupe',
    'copyright',
    'grounding',
]);
export const CheckResultName = z.enum(['pass', 'flag', 'fail', 'skip']);
export const ReviewKindName = z.enum(['flagged', 'report', 'stat', 'live_issue', 'spot_check']);
export const RejectReasonName = z.enum([
    'wrong_key',
    'ambiguous',
    'factual_error',
    'out_of_syllabus',
    'duplicate',
    'poor_quality',
    'copyright',
    'other',
]);
export const ReportReasonName = z.enum([
    'wrong_answer',
    'ambiguous',
    'typo',
    'out_of_syllabus',
    'other',
]);
export const JobStatusName = z.enum(['queued', 'running', 'paused', 'done', 'failed', 'cancelled']);
// ---- requests --------------------------------------------------------------------------------
export const ItemContentRequest = z
    .object({
    stem: z.string().trim().min(10).max(2000),
    options: z.array(z.string().trim().min(1).max(500)).length(4),
    correctIndex: z.number().int().min(0).max(3),
    explanationShort: z.string().trim().max(200).default(''),
    explanationDetailed: z.string().trim().max(4000).default(''),
})
    .strict();
export const CreateItemRequest = ItemContentRequest.extend({
    topicPath: TopicPath,
    difficulty: z.number().int().min(1).max(5),
    examKeys: z.array(ExamKey).max(12).default([]),
    computable: z.boolean().default(false),
    solverSpec: z.string().max(2000).nullable().optional(),
}).strict();
export const EditItemRequest = ItemContentRequest.partial()
    .extend({
    topicPath: TopicPath.optional(),
    difficulty: z.number().int().min(1).max(5).optional(),
})
    .strict();
export const ItemListQuery = z
    .object({
    status: ItemStatusName.optional(),
    topic: TopicPath.optional(),
    q: z.string().trim().max(200).optional(),
    jobId: Uuid.optional(),
    origin: ItemOriginName.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
})
    .strict();
export const ReasonRequest = z.object({ reason: z.string().trim().min(2).max(500) }).strict();
export const ApproveRequest = z.object({ note: z.string().trim().max(500).optional() }).strict();
export const RejectRequest = z
    .object({ reason: RejectReasonName, note: z.string().trim().max(500).optional() })
    .strict();
export const ResolveReportRequest = z
    .object({
    status: z.enum(['resolved', 'dismissed']),
    resolution: z.string().trim().min(2).max(500),
})
    .strict();
export const ReviewQueueQuery = z
    .object({
    kind: ReviewKindName.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
export const CreateJobRequest = z
    .object({
    examKey: ExamKey,
    topicPaths: z.array(TopicPath).min(1).max(30),
    difficulty: z.number().int().min(0).max(5).default(0),
    count: z.number().int().min(1).max(500),
    notes: z.string().trim().max(500).optional(),
    levelKey: z.string().trim().min(1).max(40).optional(),
    branchKey: z.string().trim().min(1).max(40).optional(),
    sourceIds: z.array(z.uuid()).max(20).optional(),
    /** Bulk run this job belongs to (ADR 0044). */
    batchLabel: z.string().trim().min(1).max(60).optional(),
})
    .strict();
export const EstimateQuery = z.object({ count: z.coerce.number().int().min(1).max(500) }).strict();
export const GenerationScheduleDto = z
    .object({
    enabled: z.boolean().default(false),
    examKey: ExamKey.optional(),
    topicPaths: z.array(TopicPath).max(50).default([]),
    maxPerDay: z.number().int().min(0).max(1000).default(40),
})
    .strict();
export const ItemImportMeta = z
    .object({
    mapping: z
        .object({
        stem: TableColumn,
        options: z.tuple([TableColumn, TableColumn, TableColumn, TableColumn]),
        answer: TableColumn,
        explanation: TableColumn.optional(),
        explanationDetailed: TableColumn.optional(),
        topicPath: TableColumn.optional(),
        difficulty: TableColumn.optional(),
        exam: TableColumn.optional(),
    })
        .strict(),
    defaults: z
        .object({
        topicPath: TopicPath.optional(),
        difficulty: z.number().int().min(1).max(5).default(3),
        examKeys: z.array(ExamKey).max(12).default([]),
    })
        .strict()
        .default({ difficulty: 3, examKeys: [] }),
})
    .strict();
// ---- responses -------------------------------------------------------------------------------
export const ItemSummaryDto = z.object({
    id: Uuid,
    status: ItemStatusName,
    origin: ItemOriginName,
    topicPath: z.string(),
    difficulty: z.number(),
    difficultyBand: z.string().nullable(),
    flags: z.array(z.string()),
    pollEligible: z.boolean(),
    miniAppOnly: z.boolean(),
    computable: z.boolean(),
    generatorModel: z.string().nullable(),
    verifierModel: z.string().nullable(),
    costPaise: z.number(),
    version: z.number(),
    generationJobId: Uuid.nullable(),
    stem: z.string(),
    options: z.array(z.string()),
    correctIndex: z.number(),
    explanationShort: z.string(),
    explanationDetailed: z.string(),
    /** A code-drawn question's picture (ADR 0050): SVG and its text alternative. */
    figure: z.object({ svg: z.string(), alt: z.string() }).nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ItemListDto = z.object({
    items: z.array(ItemSummaryDto),
    total: z.number(),
    summary: z.partialRecord(ItemStatusName, z.number()),
});
export const ItemCheckDto = z.object({
    run: z.number(),
    check: CheckName,
    result: CheckResultName,
    score: z.number().nullable(),
    details: z.record(z.string(), z.unknown()),
    costPaise: z.number(),
    aiCallId: Uuid.nullable(),
    createdAt: z.string(),
});
export const ItemDetailDto = ItemSummaryDto.extend({
    mirrors: z.record(z.string(), z.unknown()).nullable(),
    solverSpec: z.string().nullable(),
    parentItemId: Uuid.nullable(),
    checkRun: z.number(),
    checks: z.array(ItemCheckDto),
    events: z.array(z.object({
        from: ItemStatusName.nullable(),
        to: ItemStatusName,
        actorType: z.string(),
        actorId: Uuid.nullable(),
        reason: z.string().nullable(),
        payload: z.record(z.string(), z.unknown()).nullable(),
        createdAt: z.string(),
    })),
    citations: z.array(z.object({
        chunkId: Uuid.nullable(),
        sourceId: Uuid.nullable(),
        sourceTitle: z.string().nullable(),
        pageFrom: z.number().nullable(),
        excerpt: z.string().nullable(),
    })),
    applicability: z.array(z.object({ packKey: z.string(), examKey: z.string(), stageKey: z.string().nullable() })),
    stats: z.array(z.object({
        examKey: z.string(),
        n: z.number(),
        pValue: z.number().nullable(),
        pointBiserial: z.number().nullable(),
        avgTimeMs: z.number().nullable(),
        flag: z.string().nullable(),
    })),
    reports: z.array(z.object({
        id: Uuid,
        reason: ReportReasonName,
        note: z.string().nullable(),
        status: z.enum(['open', 'resolved', 'dismissed']),
        resolution: z.string().nullable(),
        createdAt: z.string(),
    })),
    reviewTasks: z.array(z.object({
        id: Uuid,
        kind: ReviewKindName,
        status: z.enum(['open', 'done', 'dismissed']),
        resolution: z.string().nullable(),
        createdAt: z.string(),
    })),
});
export const ReviewTaskDto = z.object({
    taskId: Uuid,
    kind: ReviewKindName,
    priority: z.number(),
    dueAt: z.string().nullable(),
    openedAt: z.string(),
    item: ItemSummaryDto,
    checks: z.array(z.object({
        check: CheckName,
        result: CheckResultName,
        score: z.number().nullable(),
        details: z.record(z.string(), z.unknown()),
    })),
    reports: z.array(z.object({ reason: ReportReasonName, note: z.string().nullable() })),
});
export const ReviewQueueDto = z.object({ total: z.number(), tasks: z.array(ReviewTaskDto) });
export const ReviewResultDto = z.object({
    status: ItemStatusName,
    reporters: z.object({ itemId: Uuid, userIds: z.array(Uuid), resolution: z.string() }).nullable(),
});
export const ReportDto = z.object({
    id: Uuid,
    itemId: Uuid,
    itemStatus: ItemStatusName,
    stem: z.string(),
    reason: ReportReasonName,
    note: z.string().nullable(),
    status: z.enum(['open', 'resolved', 'dismissed']),
    resolution: z.string().nullable(),
    createdAt: z.string(),
});
export const QualityInboxDto = z.object({
    days: z.number(),
    rejectReasons: z.array(z.object({ reason: z.string().nullable(), n: z.number() })),
    flagReasons: z.array(z.object({ flag: z.string(), n: z.number() })),
    byJob: z.array(z.object({
        jobId: Uuid.nullable(),
        total: z.number(),
        rejected: z.number(),
        inReview: z.number(),
        model: z.string().nullable(),
    })),
    statFlags: z.array(z.object({
        itemId: Uuid,
        examKey: z.string(),
        flag: z.string().nullable(),
        n: z.number(),
        pValue: z.number().nullable(),
    })),
    reports: z.array(ReportDto),
});
export const JobCountsDto = z.object({
    generated: z.number(),
    passed: z.number(),
    flagged: z.number(),
    rejected: z.number(),
    failed: z.number(),
    regenerated: z.number(),
    pending: z.number(),
    /** Skipped: not enough reference material for the topic (strict retrieval). */
    thin: z.number().default(0),
});
export const GenerationJobDto = z.object({
    id: Uuid,
    status: JobStatusName,
    spec: CreateJobRequest,
    counts: JobCountsDto,
    estimatePaise: z.number(),
    costPaise: z.number(),
    scheduled: z.boolean(),
    error: z.string().nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
});
export const GenerationEstimateDto = z.object({
    perItemPaise: z.number(),
    totalPaise: z.number(),
    usdInr: z.number(),
    missingPrices: z.array(z.string()),
});
export const TodayDto = z.object({
    review: z.object({
        open: z.number(),
        minutes: z.number(),
        capacityMinutes: z.number(),
        throttled: z.boolean(),
        byKind: z.record(z.string(), z.object({ open: z.number(), overdue: z.number() })),
    }),
    bank: z.object({
        byStatus: z.record(ItemStatusName, z.number()),
        servable: z.number(),
        inPipeline: z.number(),
    }),
    today: z.object({
        generated: z.number(),
        toFieldTest: z.number(),
        toReview: z.number(),
        rejected: z.number(),
        published: z.number(),
        pulled: z.number(),
    }),
    generation: z.object({
        jobs: z.record(z.string(), z.number()),
        throttledReason: z.string().nullable(),
    }),
    reportsOpen: z.number(),
    kbFailedSources: z.number(),
    aiSpend: z.object({ todayPaise: z.number(), calls: z.number() }),
    generatedAt: z.string(),
});
export const ItemImportReportDto = z.object({
    total: z.number(),
    created: z.number(),
    itemIds: z.array(Uuid),
    errors: z.array(z.object({ row: z.number(), error: z.string() })),
    warnings: z.array(z.object({ row: z.number(), warning: z.string() })).default([]),
    /** Every row was checked and none saved (the admin CLI's `item-import --dry-run`). */
    dryRun: z.boolean().default(false),
});
export const ItemTablePreviewDto = z.object({
    headers: z.array(z.string()),
    rows: z.array(z.array(z.string())),
    total: z.number(),
});
export const IdDto = z.object({ id: Uuid });
export const NewItemDto = z.object({ newItemId: Uuid });
export const TakedownDto = z.object({ retired: z.number() });
/** Public website: live counts of published questions per exam, and one real sample per exam. */
export const WebShowcaseDto = z.object({
    exams: z.array(z.object({
        examKey: z.string(),
        questions: z.number().int().nonnegative(),
        subjects: z.array(z.object({ subject: z.string(), questions: z.number().int().nonnegative() })),
    })),
    samples: z.array(z.object({
        itemId: z.string(),
        examKey: z.string(),
        subject: z.string(),
        stem: z.string(),
        options: z.array(z.string()),
        correctIndex: z.number().int(),
        explanationShort: z.string(),
        explanationDetailed: z.string(),
    })),
    /** The `mocks_enabled` flag: while false the website leaves every mock line out. */
    mocksEnabled: z.boolean().optional(),
});
/** KB coverage per topic for one exam: reference pieces and live questions. */
export const CoverageQuery = z.object({ exam: z.string().trim().min(1).max(40) }).strict();
export const CoverageRowDto = z.object({
    topicPath: z.string(),
    name: z.string(),
    depth: z.number().int(),
    chunks: z.number().int(),
    questions: z.number().int(),
    /** Under 2 reference pieces: strict generation will skip this topic. */
    thin: z.boolean(),
});
export const CoverageDto = z.object({ exam: z.string(), rows: z.array(CoverageRowDto) });
/** Question-bank settings (ADR 0044). */
export const QuestionBankSettingsDto = z
    .object({
    /** Generation pauses once today's (IST) content AI spend reaches this (₹). */
    dailySpendLimitInr: z.number().int().min(1).max(100_000),
    /** Share of auto-passed questions that also get a spot-check (0–1). */
    spotCheckRate: z.number().min(0).max(1),
    /**
     * The AI arbiter passes a flagged question only at or above this confidence (ADR 0045). Never
     * below the hard bar, 0.95 (`HARD_KEEP_MIN_CONFIDENCE`), so the API refuses less.
     */
    arbiterMinConfidence: z.number().min(0.95).max(1).optional(),
    /** Questions written at once, 1–6; null = the server's GENERATION_CONCURRENCY (ADR 0045). */
    generationConcurrency: z.number().int().min(1).max(6).nullable().optional(),
})
    .strict();
export const QuestionBankSettingsViewDto = QuestionBankSettingsDto.extend({
    arbiterMinConfidence: z.number(),
    spentTodayPaise: z.number(),
    /** The concurrency in effect: the setting, else the server default. */
    effectiveConcurrency: z.number().int(),
});
/** Topics where every new question waits for a reviewer (ADR 0044). */
export const FullReviewTopicDto = z.object({
    topicPath: z.string(),
    since: z.string(),
    reason: z.string(),
});
export const FullReviewRequest = z
    .object({
    topicPath: TopicPath,
    on: z.boolean(),
    reason: z.string().trim().min(1).max(200).optional(),
})
    .strict();
//# sourceMappingURL=items.js.map